import { IconDanger } from 'shared/assets/icons';

export function FieldError({ id, children }) {
  if (!children) {
    return null;
  }
  return (
    <p id={id} className="field-error" role="alert">
      <IconDanger />
      <span>{children}</span>
    </p>
  );
}

export function Field({
  id,
  label,
  hint,
  error,
  required = false,
  children,
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="field">
      {label ? (
        <div className="field-label-row">
          <label className="field-label" htmlFor={id}>{label}</label>
          {required ? <span className="required-mark" aria-hidden="true">*</span> : null}
        </div>
      ) : null}
      {children}
      {hint ? <p id={hintId} className="field-hint">{hint}</p> : null}
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
}

function describedBy(hint, error, extra) {
  const hintId = hint ? `${extra?.id}-hint` : undefined;
  const errorId = error ? `${extra?.id}-error` : undefined;
  return [extra?.describedBy, hintId, errorId].filter(Boolean).join(' ') || undefined;
}

export function TextInput({
  id,
  label,
  hint,
  error,
  type = 'text',
  className,
  describedBy: describedByProp,
  required,
  ltr = false,
  ...props
}) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <input
        id={id}
        type={type}
        className={['input', error ? 'input-error' : '', ltr ? 'input-ltr' : '', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(hint, error, { id, describedBy: describedByProp })}
        dir={ltr ? 'ltr' : undefined}
        required={required}
        {...props}
      />
    </Field>
  );
}

export function TextArea({
  id,
  label,
  hint,
  error,
  className,
  required,
  ...props
}) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <textarea
        id={id}
        className={['input', 'textarea', error ? 'input-error' : '', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(hint, error, { id })}
        required={required}
        {...props}
      />
    </Field>
  );
}

export function ChoiceSet({
  id,
  label,
  hint,
  error,
  required = false,
  children,
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const described = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <fieldset
      className="field choice-set"
      aria-describedby={described}
      aria-invalid={error ? true : undefined}
    >
      <legend className="field-label">
        {label}
        {required ? <span className="required-mark" aria-hidden="true"> *</span> : null}
      </legend>
      <div className="channel-set">{children}</div>
      {hint ? <p id={hintId} className="field-hint">{hint}</p> : null}
      <FieldError id={errorId}>{error}</FieldError>
    </fieldset>
  );
}

export function SelectInput({
  id,
  label,
  hint,
  error,
  children,
  className,
  required,
  ...props
}) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <select
        id={id}
        className={['input', error ? 'input-error' : '', className].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(hint, error, { id })}
        {...props}
      >
        {children}
      </select>
    </Field>
  );
}
